import {
    createContext,
    PropsWithChildren,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { SimplePopup } from './components/SimplePopup';
import {
    PopupConfig,
    PopupContextValues,
    TogglePopupConfig,
    UseTogglePopupResult,
} from './types';

const PopupContext = createContext<PopupContextValues>({
    open: () => null,
    close: () => null,
});

interface PopupState {
    popupId: number;
    config: PopupConfig | null;
    visible: boolean;
}

const INITIAL_POPUP_STATE: PopupState = {
    popupId: 0,
    config: null,
    visible: false,
};

export function PopupProvider({ children }: PropsWithChildren): ReactNode {
    const [popupState, setPopupState] =
        useState<PopupState>(INITIAL_POPUP_STATE);
    const configRef = useRef<PopupConfig | null>(null);
    const pendingReopenRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(
        () => () => {
            if (pendingReopenRef.current !== null) {
                clearTimeout(pendingReopenRef.current);
            }
        },
        [],
    );

    const cancelPendingReopen = useCallback((): void => {
        if (pendingReopenRef.current !== null) {
            clearTimeout(pendingReopenRef.current);
            pendingReopenRef.current = null;
        }
    }, []);

    const fireOnClose = useCallback((): void => {
        const onClose = configRef.current?.onClose;
        configRef.current = null;
        onClose?.();
    }, []);

    const closePopup = useCallback((): void => {
        cancelPendingReopen();
        fireOnClose();
        setPopupState((current) => {
            if (!current.config || !current.visible) {
                return current;
            }

            return {
                ...current,
                config: null,
                visible: false,
            };
        });
    }, [cancelPendingReopen, fireOnClose]);

    const openPopup = useCallback(
        (config: PopupConfig): void => {
            cancelPendingReopen();
            fireOnClose();
            configRef.current = config;

            setPopupState((current) => {
                const nextId = current.popupId + 1;

                if (current.visible) {
                    pendingReopenRef.current = setTimeout(() => {
                        pendingReopenRef.current = null;
                        setPopupState((prev) => ({
                            ...prev,
                            config,
                            visible: true,
                        }));
                    }, 0);

                    return {
                        ...current,
                        popupId: nextId,
                        config,
                        visible: false,
                    };
                }

                return {
                    ...current,
                    popupId: nextId,
                    config,
                    visible: true,
                };
            });
        },
        [cancelPendingReopen, fireOnClose],
    );

    const handleHide = useCallback(
        (popupId: number): void => {
            setPopupState((current) => {
                if (current.popupId !== popupId || !current.config) {
                    return current;
                }

                fireOnClose();

                return {
                    ...current,
                    config: null,
                    visible: false,
                };
            });
        },
        [fireOnClose],
    );

    return (
        <PopupContext.Provider
            value={{
                open: openPopup,
                close: closePopup,
            }}
        >
            {children}
            <SimplePopup
                config={popupState.config}
                visible={popupState.visible}
                onHide={() => {
                    handleHide(popupState.popupId);
                }}
            />
        </PopupContext.Provider>
    );
}

export function usePopup(): PopupContextValues {
    return useContext(PopupContext);
}

export function useTogglePopup(): UseTogglePopupResult {
    const [visible, setVisible] = useState(false);
    const visibleRef = useRef(visible);
    visibleRef.current = visible;

    const { open, close } = usePopup();

    const toggle = useCallback(
        (
            e: React.MouseEvent<HTMLButtonElement>,
            config: TogglePopupConfig,
        ): void => {
            e.preventDefault();
            e.stopPropagation();

            if (visibleRef.current) {
                close();
                return;
            }

            setVisible(true);
            open({
                ...config,
                target: e.currentTarget,
                onClose: () => setVisible(false),
            });
        },
        [open, close],
    );

    return { visible, toggle };
}
